import { ClientDTO } from './../../models/models.swagger.generated';
import { ShowInactiveService } from './show-inactive.service';
import { DataManager } from '@syncfusion/ej2-data';
import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent, NotifyArgs, SaveEventArgs, SearchSettingsModel } from '@syncfusion/ej2-angular-grids';
import { ClientService } from '../../services/client/client.service';
import { GridConstantsFactory } from '../syncfusion/grid-constants-factory';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { FormHelper } from '../form-helper';
import { SfAction, SfRequestTypes } from '../../models/syncfusion-data';

@Component({
  selector: 'app-base',
  template: ''
})
export abstract class GridBaseComponent {
  @ViewChild('grid')
  public grid: GridComponent;
  public filterSettings = GridConstantsFactory.createStandardFilterSettings();
  public editSettings = GridConstantsFactory.createStandardEditSettings();
  public pageSettings = GridConstantsFactory.createStandardPageSettings();
  public gridCommands = GridConstantsFactory.createStandardGridCommands();
  public searchOptions: SearchSettingsModel;
  public gridEditForm: UntypedFormGroup;
  public isActiveFilter = GridConstantsFactory.createCheckBoxFilter();
  public hasBillingFilter = GridConstantsFactory.createCheckBoxFilter();
  public dateFilter = GridConstantsFactory.createDateFilter();
  public clientSelected$ = new Subject<number>();
  public showSaveButton = true;

  /* data to be provided by childclass */
  public data: DataManager;
  protected isActiveColumnName: string;
  protected editDialogTitle = 'editDialogTitle';
  protected addDialogTitle = 'addDialogTitle';
  protected addIndexFindColumnName = 'name';
  protected dialogConfig: any = {};
  protected additionalShowInactiveColumns: string[] = [];
  protected considerDisabledFieldsForFormChanges: boolean = false;
  /* data to be provided by childclass */

  protected unsubscribeSubject = new Subject<void>();
  protected formInitialValue;
  protected idOfEditedObject: number = null;

  private _showInactive: boolean = null;

  constructor(
    protected clientService: ClientService,
    protected translateService: TranslateService,
    protected showInactiveService: ShowInactiveService
  ) {}

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public get showInactive(): boolean {
    // due to a bug in syncfusion we can only present a value that is only initialized to syncfusion
    // but we want to still keep the showInactive information in the service
    if (this._showInactive == null) {
      this._showInactive = this.showInactiveService.showInactive;
    }
    return this._showInactive;
  }
  public set showInactive(value: boolean) {
    if (this.showInactiveService.showInactive !== value) {
      this.showInactiveService.showInactive = value;
      // syncfusion uses [visible] only to initialize the grid
      this.grid.getColumnByField(this.isActiveColumnName ?? 'active').visible = value;
      if (this.additionalShowInactiveColumns.length > 0) {
        this.additionalShowInactiveColumns.forEach(column => {
          this.grid.getColumnByField(column).visible = value;
        });
      }
      this.grid.refreshColumns();
    }
  }

  public formatIsActive(data: any, isActiveField: string = 'isActive'): boolean {
    if (data && data[isActiveField]) {
      return this.translateService.instant('i18n.shared.fields.active');
    }
    return this.translateService.instant('i18n.shared.inactive');
  }

  public onCreated(): void {
    // this methods needs to be called in the grid for (create) event when experiencing bugs caused by the undefined url -> eg. issues where data for response is expected
    const dataModule = (this.grid as any).getDataModule();
    (this.grid as any).getDataModule().isRemote = () => this.data?.dataSource.offline !== true && this.data?.dataSource.url === undefined;
  }

  public onAdd() {
    this.grid.addRecord();
  }

  public onClientSelected(client: ClientDTO) {
    this.clientSelected$.next(client && client.id ? client.id : null);
    this.grid?.refresh();
  }

  public onSearchTextChanged(searchText: string) {
    this.grid.search(searchText);
  }

  public actionComplete(args: any) {
    if ((args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add)) {
      const dialog = args.dialog;
      if (this.dialogConfig.width) {
        dialog.width = this.dialogConfig.width;
      } else {
        dialog.width = 780;
      }

      if (this.dialogConfig.class) {
        dialog.cssClass = this.dialogConfig.class;
      }

      dialog.header = args.requestType === SfRequestTypes.BeginEdit
        ? this.translateService.instant(this.editDialogTitle, this.getEditDialogTitleParams(args.rowData))
        : this.translateService.instant(this.addDialogTitle);

      const saveBtn = dialog.btnObj[0];
      if (!this.showSaveButton) {
        dialog.buttons = dialog.buttons.filter(btn => !btn.buttonModel.isPrimary);
      }

      if (this.gridEditForm) {
        this.gridEditForm?.statusChanges
          .pipe(
            startWith(this.gridEditForm.status),
            takeUntil(this.unsubscribeSubject),
          )
          .subscribe(status => {
            saveBtn.disabled = (status !== 'VALID') || (JSON.stringify(this.formInitialValue) === JSON.stringify(this.considerDisabledFieldsForFormChanges ? this.gridEditForm.getRawValue() : this.gridEditForm.value));
          });

        FormHelper.updateTreeValidity(this.gridEditForm, !!(this.gridEditForm.get('id') ?? this.gridEditForm.get('userId'))?.value);
      }
    } else if (args.requestType === SfRequestTypes.Save) {
      if (args.action === SfAction.Add) {
        const argz = args as NotifyArgs;
        const index = argz.rows?.findIndex((row) => this.addObjectCompare(args.data, row.data));
        if (index < 0) {
          this.grid?.clearRowSelection();
        } else {
          this.grid?.selectRow(index);
        }
      }
      else if (args.action === SfAction.Edit) {
        this.idOfEditedObject = args.rowData?.id;
        // refresh is executed automatically after add and delete
        this.grid?.refresh();
      }
      this.gridEditForm.markAsPristine();
    } else if (args.requestType === SfRequestTypes.Refresh && this.idOfEditedObject) {
      const index = this.grid?.getRowIndexByPrimaryKey(this.idOfEditedObject);
      this.grid?.selectRow(index);
      this.idOfEditedObject = null;
    } else if (args.requestType === SfRequestTypes.Delete) {
      const id = (args.data && args.data.length) ? args.data[0].id : null;
      const argz = args as NotifyArgs;
      const index = argz.rows?.findIndex((row) => (row.data as any).id === args.data.id);
      if (index < 0) {
        this.grid?.clearRowSelection();
      } else {
        this.grid?.selectRow(index);
      }
      this.gridEditForm?.markAsPristine();
    } else if (args.requestType === SfRequestTypes.Cancel) {
      this.gridEditForm?.markAsPristine();
    }
  }

  public actionBegin(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add) {
      this.initializeEditForm(args.rowData);
      if (this.considerDisabledFieldsForFormChanges) {
        this.formInitialValue = JSON.parse(JSON.stringify(this.gridEditForm.getRawValue()));
      } else {
        this.formInitialValue = JSON.parse(JSON.stringify(this.gridEditForm.value));
      }
    }
    else if (args.requestType === SfRequestTypes.Save) {
      if (this.gridEditForm.valid) {
        args.data = this.getDataToSave();
      } else {
        args.cancel = true;
      }
    } else if (args.requestType === SfRequestTypes.Cancel) {
      this.unsubscribeSubject.next();
    }
  }

  // override this method if a more complex method is needed
  // to compare added item with the ones in the rows (added item does not have id set)
  protected addObjectCompare(addedObject: any, rowObject: any): boolean {
    if (this.addIndexFindColumnName) {
      return addedObject[this.addIndexFindColumnName] === rowObject[this.addIndexFindColumnName];
    }
    return false;
  }

  // override this method if different data or data structure has to be provided for add / edit
  protected getDataToSave(): any {
    return this.gridEditForm.value;
  }

  protected abstract initializeEditForm(data: any): void;
  protected abstract getEditDialogTitleParams(rowData: any): any;
}
